import {
    Typography,
    FormControlLabel,
    Switch,
    Grid,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { FormikProps } from 'formik';
import { CustomerTypes } from '../../../../../types';
import { SettingsFieldCard } from './settings-field-card';

interface Props {
    formik: FormikProps<CustomerTypes.OnboardingForm>;
    /**
     * Every field on this tab is a v2 catalog setting, so the whole tab is
     * read-only. Defaults to disabled so a caller that forgets the prop
     * cannot reopen a legacy write path.
     */
    disabled?: boolean;
}

export const ShiftSettingsTab: React.FC<Props> = ({ formik, disabled = true }) => {
    const { values, touched, errors, getFieldProps } = formik;

    const handleFieldChange = (field: string, value: any) => {
        formik.setFieldValue(field, value);
    };

    const isShiftEnabled = values?.shiftConfig === "Enabled";

    return (
        <>
            <Typography fontWeight={500} fontSize={18} mt={2}>
                Daily Shift
            </Typography>
            <Typography fontSize={14} mb={1} color="text.secondary">
                Allow users to start a daily shift timer to track the start and end of their work day.
            </Typography>

            {/* Shift Enable */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={isShiftEnabled}
                            onChange={(e) => {
                                handleFieldChange("shiftConfig", e.target.checked ? "Enabled" : "Disabled");
                                if (!e.target.checked) {
                                    handleFieldChange("escalateShift", false);
                                    handleFieldChange("shiftDispatchEnabled", false);
                                }
                            }}
                            disabled={disabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Shift</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable the Daily Shift feature for this organization. This makes the feature available; control is handled at the Group and Team level.
                    </Typography>
                </Grid>
            </SettingsFieldCard>

            {/* Auto Escalate Shifts */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.escalateShift ?? false}
                            onChange={(e) => handleFieldChange('escalateShift', e.target.checked)}
                            disabled={disabled || !isShiftEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 2 / 3 }}>
                    <Typography fontWeight="600" fontSize={16}>Auto Escalate Expired Shifts</Typography>
                    <Typography color="textSecondary" variant="body2">
                        When a shift expires without being ended, automatically escalate the alert to POM Dispatch after a configurable timer.
                    </Typography>
                </Grid>
                <Grid item sx={{ mt: 1 }}>
                    <Select
                        {...getFieldProps('escalateShiftTimeout')}
                        disabled={disabled || !isShiftEnabled || !values?.escalateShift}
                        sx={{ width: 150 }}
                        error={touched.escalateShiftTimeout && Boolean(errors.escalateShiftTimeout)}
                        onChange={(e) => handleFieldChange('escalateShiftTimeout', Number(e.target.value))}
                        value={values?.escalateShiftTimeout || 1}
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(option => (
                            <MenuItem key={option} value={option}>
                                {option} {option === 1 ? 'minute' : 'minutes'}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {touched.escalateShiftTimeout && errors.escalateShiftTimeout}
                    </FormHelperText>
                </Grid>
            </SettingsFieldCard>

            {/* Shift Dispatch Integration */}
            <SettingsFieldCard>
                <FormControlLabel
                    control={
                        <Switch
                            checked={values?.shiftDispatchEnabled ?? false}
                            onChange={(e) => handleFieldChange('shiftDispatchEnabled', e.target.checked)}
                            disabled={disabled || !isShiftEnabled}
                        />
                    }
                    label=""
                    sx={{ my: "auto" }}
                />
                <Grid maxWidth="sm" sx={{ width: 1 }}>
                    <Typography fontWeight="600" fontSize={16}>Shift Dispatch Integration</Typography>
                    <Typography color="textSecondary" variant="body2">
                        Enable POM Dispatch integration for shift alerts. When enabled, expired and escalated shift alerts are sent to the dispatch team.
                    </Typography>
                </Grid>
            </SettingsFieldCard>
        </>
    );
};
